import { type FunctionComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';
import { SharedFAQAnswer } from './SharedFAQAnswer.tsx';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { type SharedFAQQuestion_question$data } from './__generated__/SharedFAQQuestion_question.graphql.ts';
import dibsCss from 'dibs-css';
import styles from './SharedFAQQuestion.scss';

type Props = {
    question: SharedFAQQuestion_question$data | null | undefined;
    className?: string;
};

const SharedFAQQuestionComponent: FunctionComponent<Props> = ({ question, className }) => {
    const { title, url, answerCount, id, answers } = question || {};
    if (title && url && id && answers && answerCount) {
        return (
            <li
                className={classnames(
                    className,
                    styles.question,
                    dibsCss.overflowHidden,
                    dibsCss.pySmall,
                    dibsCss.px0,
                    dibsCss.borderB,
                    dibsCss.borderBSolid,
                    dibsCss.borderBDolphin
                )}
                key={id}
            >
                <div className={dibsCss.mbSmall}>
                    <Link
                        href={url}
                        className={classnames(
                            styles.questionTitle,
                            dibsCss.sassyFontBodyLargeHeavy
                        )}
                        dataTn="faq-question"
                    >
                        {title}
                    </Link>
                    <div className={dibsCss.sassyFontBody} data-tn="faq-answer-count">
                        <FormattedMessage
                            id="SharedFAQ.numAnswers"
                            defaultMessage="{answerCount, plural, one {# Answer} other {# Answers}}"
                            values={{ answerCount }}
                        />
                    </div>
                </div>
                <ExpandingAreaWithReadMore buttonClass={styles.button} collapsedHeight={206}>
                    {answers.map(answer =>
                        answer?.serviceId ? (
                            <SharedFAQAnswer answer={answer} key={answer.serviceId} />
                        ) : null
                    )}
                </ExpandingAreaWithReadMore>
            </li>
        );
    }
    return null;
};

export const SharedFAQQuestion = createFragmentContainer(SharedFAQQuestionComponent, {
    question: graphql`
        fragment SharedFAQQuestion_question on QuestionType {
            id
            url
            title
            answerCount
            answers {
                serviceId
                ...SharedFAQAnswer_answer
            }
        }
    `,
});
